import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import Alert from 'core/components/Alert'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import Progress from 'core/components/progress/Progress'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import { Route } from 'core/plugins/route'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import Button from 'pf9-ui-components/built/elements/button/Button'
import Modal, { ModalProps } from 'pf9-ui-components/built/elements/modal/Modal'
import Text from 'pf9-ui-components/built/elements/Text'
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useReactRouter from 'use-react-router'
import { isNilOrEmpty } from 'utils/fp'

const imagePath = '/ui/images/circle-check.svg'

interface Error {
  title?: string
  message?: string
  response?: { data?: { error?: { message?: string } } }
}

interface BaseModalFormProps extends Omit<ModalProps, 'open' | 'onClose'> {
  onSubmit: () => void
  onClose: () => void
  disableSubmit?: boolean
  isOnSubmitValidationRequired?: boolean
  fieldSetter?: any
  error?: Error
  resetError?: () => void
  customErrorComponent?: React.ReactNode
  customWarningComponent?: React.ReactNode
  loading?: boolean
  loadingMessage?: string
  submitting?: boolean
  initialValues?: Record<string, unknown>
  withAddonManager?: boolean
  clearOnSubmit?: boolean
  closeButtonText?: string
  buttonAlignment?: 'left' | 'right'
  submitTitle?: string
  ValidatedFormComponent?: typeof ValidatedForm
  customStepProgressComponent?: React.ReactNode
  params?: Record<string, unknown>
  showProgress?: boolean
  isProgressComplete?: boolean
  closeProgressScreen?: () => void
  resetProgress?: () => void
}

interface PropsWithOpenRoute extends BaseModalFormProps {
  open?: undefined
  route: Route
}

interface PropsWithOpenFlag extends BaseModalFormProps {
  open: boolean
  route?: undefined
}

export type ModalStep = {
  stepId: number
  component: React.ReactNode
  nextButtonText?: string
  backButtonText?: string
  onNext?: () => void
  onBack?: () => void
  headerTitle?: string
  description?: string
  stepValidator?: () => { success: boolean; error?: Error }
}

type ModalFormProps = (PropsWithOpenRoute | PropsWithOpenFlag) & {
  steps?: ModalStep[]
  activeStep?: number
}

export default function StepperModalForm({
  children,
  onSubmit,
  onClose,
  disableSubmit = false,
  isOnSubmitValidationRequired = true,
  closeButtonText = 'Cancel',
  fieldSetter = null,
  submitting = false,
  loading = false,
  loadingMessage = 'Loading',
  error: errorProp,
  resetError: resetErrorProp,
  customErrorComponent = null,
  customWarningComponent = null,
  route,
  open,
  withAddonManager,
  initialValues = {},
  steps = [],
  activeStep = 0,
  buttonAlignment = 'left',
  submitTitle,
  ValidatedFormComponent = ValidatedForm,
  customStepProgressComponent = null,
  params,
  showProgress = false,
  isProgressComplete = false,
  closeProgressScreen,
  resetProgress,
  ...props
}: PropsWithChildren<ModalFormProps>) {
  const classes = useStyles()
  const { match } = useReactRouter()

  // State
  const [currentStep, setCurrentStep] = useState(activeStep)
  const [visitedStep, setVisitedStep] = useState(activeStep)
  const [error, setError] = useState(null)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [isFormValid, setIsFormValid] = useState(true)

  // Refs
  const validatorRef = useRef<{ validate: () => boolean }>({ validate: () => true })
  const submitFuncRef = useRef<any>(null)

  // Derived State
  const currentStepData = useMemo(() => steps?.[currentStep], [steps, currentStep])
  const toOpen = useMemo(() => (route ? route.pattern.match(match.url) : open), [
    match.url,
    open,
    route,
  ])
  const isLastStep = useMemo(() => currentStep === steps.length - 1, [currentStep, steps])
  const isFirstStep = useMemo(() => currentStep === 0, [currentStep])

  const setSubmitFuncRef = (validate) => {
    validatorRef.current = { validate }
  }

  // TODO: Add tracking events similar to those found in ModalForm
  // for moving steps, validation errors, and submission
  // Error Handler
  useEffect(() => {
    if (errorProp) setError(errorProp)
  }, [errorProp])

  // Validate form on params change when form is invalid
  useEffect(() => {
    if (!isNilOrEmpty(params) && !isFormValid) {
      validateForm()
    }
  }, [params])

  // Form Validations before moving to next step
  const validateForm = useCallback(async () => {
    // Error handling for previous step error
    if (!!errorProp) {
      setError(errorProp)
      return false
    }

    // Error handling for form validation
    const isValid = await validatorRef.current.validate()
    if (!isValid) {
      setIsFormValid(false)
    }

    // Error handling for step validation
    const { success = true, error = null } = (currentStepData?.stepValidator?.() || {}) as {
      success: boolean
      error?: Error
    }
    if (!isNilOrEmpty(error)) {
      setIsFormValid(false)
      setError({
        message: error?.message,
      })
    }

    // Return false if any validation fails
    if (!success || !isValid) return false

    // Set form valid state
    setIsFormValid(true)
    return true
  }, [currentStepData, validatorRef, errorProp])

  // Reset error state
  const resetErrorStates = useCallback(() => {
    resetErrorProp?.()
    setIsFormValid(true)
    setError(null)
  }, [resetErrorProp])

  // Next Click Handler
  const handleNext = useCallback(async () => {
    if (!(await validateForm())) return

    // Reset error state
    resetErrorStates()

    // Call onNext callback if provided
    currentStepData?.onNext?.()

    // Move to next step or submit form
    const nextStep = currentStep + 1
    setVisitedStep((prev) => Math.max(prev, nextStep))
    setCurrentStep(nextStep)
    const modalBody = document.querySelector('.modal-body')
    modalBody.scrollTo(0, 0)
  }, [currentStep, currentStepData])

  // Submit Click Handler
  const handleSubmit = useCallback(async () => {
    if (isOnSubmitValidationRequired) {
      // Reset error state
      resetErrorStates()
      if (!(await validateForm())) return
    }
    onSubmit()
  }, [validateForm, onSubmit, isOnSubmitValidationRequired])

  // Back Click Handler
  const handleBack = useCallback(() => {
    if ((showProgress || isProgressComplete) && !!error) {
      resetProgress?.()
      closeProgressScreen?.()
      return
    }
    resetProgress?.()
    currentStepData?.onBack?.()
    setCurrentStep((prev) => prev - 1)
    const modalBody = document.querySelector('.modal-body')
    modalBody.scrollTo(0, 0)
    // Reset error state
    resetErrorStates()
  }, [
    currentStep,
    currentStepData,
    showProgress,
    isProgressComplete,
    error,
    resetProgress,
    closeProgressScreen,
    resetErrorStates,
  ])

  // Handle step click from stepper
  const handleStepClick = useCallback(
    async (index: number) => {
      if (index <= visitedStep && index !== currentStep) {
        if (!(await validateForm())) return
        setCurrentStep(index)
        const modalBody = document.querySelector('.modal-body')
        modalBody.scrollTo(0, 0)
      } else {
        return
      }
    },
    [currentStep, visitedStep, validateForm],
  )

  const handleClose = () => setShowConfirmationDialog(true)
  const handleCancel = () => setShowConfirmationDialog(false)
  const handleConfirm = () => {
    setShowConfirmationDialog(false)
    setCurrentStep(0)
    setVisitedStep(0)
    onClose()
  }

  // TODO: What is use of this?
  function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T>()
    useEffect(() => {
      ref.current = value
    }, [value])
    return ref.current
  }
  const prevToOpen = usePrevious(toOpen)

  useEffect(() => {
    if (!prevToOpen && toOpen) {
      setCurrentStep(0)
      setVisitedStep(0)
    }
  }, [toOpen, prevToOpen])

  return (
    <>
      <ConfirmationDialog
        open={showConfirmationDialog}
        title="Discard Changes?"
        text="Are you sure you want to leave?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <Modal
        className={clsx(classes.modal, {
          [classes.modalWithProgressPadding]: showProgress,
        })}
        open={toOpen}
        onClose={handleClose}
        footer={
          <div className={classes.footerContainer}>
            <div
              className={clsx(classes.footer, { [classes.footerLeft]: buttonAlignment === 'left' })}
            >
              <Button variant="secondary" type="button" onClick={handleClose} disabled={submitting}>
                {closeButtonText}
              </Button>
              {!isFirstStep && (
                <Button
                  icon="angle-left"
                  variant="secondary"
                  type="button"
                  onClick={handleBack}
                  disabled={submitting || isFirstStep}
                >
                  {currentStepData?.backButtonText || 'Back'}
                </Button>
              )}
              <Button
                variant="primary"
                rightIcon={isLastStep ? null : 'angle-right'}
                onClick={
                  isProgressComplete
                    ? handleConfirm
                    : showProgress
                    ? handleClose
                    : isLastStep
                    ? handleSubmit
                    : handleNext
                }
                loading={submitting}
                disabled={isLastStep && disableSubmit}
              >
                {isProgressComplete || showProgress
                  ? 'Finish'
                  : isLastStep
                  ? submitTitle || 'Submit'
                  : currentStepData?.nextButtonText || 'Next'}
              </Button>
              {!isFormValid && <ErrorMessage>Please fill all the required fields</ErrorMessage>}
            </div>
          </div>
        }
        {...props}
      >
        {!showProgress && !isProgressComplete ? (
          <Progress loading={loading} message={loadingMessage} renderContentOnMount={false}>
            <div className={classes.container}>
              <div className={classes.stepperContainer}>
                {steps.map((_, index) => (
                  <div key={index} className={classes.stepItem}>
                    <div
                      className={clsx(classes.stepCircle, {
                        [classes.activeStepCircle]: currentStep === index,
                      })}
                      style={{ cursor: index <= visitedStep ? 'pointer' : 'default' }}
                      onClick={() => handleStepClick(index)}
                    >
                      {index < currentStep ? <img alt="Stepper Icon" src={imagePath} /> : index + 1}
                    </div>
                    {index < steps.length - 1 && <div className={classes.stepLine} />}
                  </div>
                ))}
              </div>
              <div className={classes.verticalDivider} />
              <div className={classes.formContainer}>
                <div className={classes.errorContainer}>
                  {customWarningComponent && customWarningComponent}
                  {!!error &&
                    (customErrorComponent ? (
                      customErrorComponent
                    ) : (
                      <Alert variant="error" message={error?.message} title={error?.title} />
                    ))}
                </div>
                <div className={classes.formSectionHeader}>
                  <Text variant="caption1">{currentStepData?.headerTitle}</Text>
                  <Text variant="body2">{currentStepData?.description}</Text>
                </div>
                <ValidatedFormComponent
                  triggerSubmit={setSubmitFuncRef}
                  elevated={false}
                  fieldSetter={fieldSetter}
                  initialValues={initialValues}
                  withAddonManager={withAddonManager}
                  clearOnSubmit={props.clearOnSubmit}
                >
                  {currentStepData?.component}
                </ValidatedFormComponent>
              </div>
            </div>
          </Progress>
        ) : (
          customStepProgressComponent
        )}
      </Modal>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      height: 'calc(100vh - 146px)', // adjust based on your modal height
      overflow: 'hidden', // stop scrolling at the modal-body level
    },
    '& > .modal-body > .progress-root > .progressContent': {
      overflowY: 'auto',
    },
  },
  modalWithProgressPadding: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
  },
  container: {
    display: 'flex',
    height: '100%',
  },
  stepperContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    backgroundColor: '#fff',
    zIndex: 2,
    minWidth: 80,
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  stepCircle: {
    height: 32,
    width: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    backgroundColor: colors.grey[200],
    color: colors.grey[500],
  },
  activeStepCircle: {
    backgroundColor: colors.blue[700],
    color: 'white',
  },
  stepLine: {
    width: 2,
    height: 24,
    backgroundColor: colors.grey[300],
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  verticalDivider: {
    width: 1,
    backgroundColor: colors.grey[200],
    height: '100%',
  },
  formContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    overflowY: 'auto',

    // Ensure the form takes up the remaining space and can scroll
    '& > form': {
      height: '100%',
    },
  },
  errorContainer: {
    marginBottom: theme.spacing(2),
  },
  formSectionHeader: {
    display: 'grid',
    gridGap: theme.spacing(0.5),
    border: `1px solid ${colors.grey[200]}`,
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  footerContainer: {
    position: 'sticky',
    bottom: 0,
    background: 'white',
    padding: theme.spacing(2, 3),
    zIndex: 2,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  footerLeft: {
    justifyContent: 'flex-start',
  },
}))
